import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import axios from 'axios';
import {
    User,
    Mail,
    Phone,
    MapPin,
    Calendar,
    RefreshCw,
    ArrowLeft
} from 'lucide-react';
import MetaData from '../layouts/MetaData';
import { getFormattedImageUrl } from '../../utils/ImageUtil';

const AdminProfile = () => {
    const navigate = useNavigate();
    const { admin } = useSelector(state => state.adminState);
    
    const [profileData, setProfileData] = useState(null);
    const [loading, setLoading] = useState(true);
    
    useEffect(() => {
        const fetchAdminProfile = async () => {
            try {
                setLoading(true);
                const { data } = await axios.get('/api/v1/admin/profile', {
                    withCredentials: true
                });
                
                if (data.success) {
                    setProfileData(data.admin);
                }
            } catch (error) {
                console.error('Error fetching admin profile:', error);
                toast.error(error.response?.data?.message || 'Error fetching admin profile');
            } finally {
                setLoading(false);
            }
        };
        
        fetchAdminProfile();
    }, []);
    
    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString('en-US', options);
    };
    
    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <RefreshCw className="h-6 w-6 text-[#dc2626] animate-spin" />
                <span className="ml-2 text-sm">Loading admin profile...</span>
            </div>
        );
    }
    
    return (
        <>
            <MetaData title="Admin Profile" />
            <div className="min-h-screen bg-gray-50 py-6">
                <div className="max-w-4xl mx-auto px-3 sm:px-4 lg:px-6">
                    <button
                        onClick={() => navigate('/admin/dashboard')}
                        className="mb-4 flex items-center gap-1.5 text-gray-600 hover:text-[#dc2626] transition-colors text-sm"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        <span>Back to Dashboard</span>
                    </button>
                    
                    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                        {/* Header */}
                        <div className="bg-[#dc2626] px-4 py-6">
                            <div className="flex flex-col sm:flex-row items-center gap-4">
                                <div className="h-20 w-20 rounded-full bg-white flex items-center justify-center shadow-lg overflow-hidden">
                                    {profileData?.profilePicture ? (
                                        <img 
                                            src={getFormattedImageUrl(profileData.profilePicture)} 
                                            alt={profileData.name}
                                            className="h-full w-full object-cover"
                                            onError={(e) => {
                                                console.error("Image load error");
                                                e.target.onerror = null;
                                                e.target.parentElement.innerHTML = '<div class="h-full w-full flex items-center justify-center"><User class="h-10 w-10 text-[#dc2626]" /></div>';
                                            }}
                                        />
                                    ) : (
                                        <User className="h-10 w-10 text-[#dc2626]" />
                                    )}
                                </div>
                                
                                <div className="text-center sm:text-left">
                                    <h1 className="text-xl font-bold text-white font-['Crimson_Text']">
                                        {profileData?.name || 'Admin User'}
                                    </h1>
                                    <p className="text-white/90 mt-0.5 text-sm">Administrator</p>
                                </div>
                            </div>
                        </div>
                        
                        {/* Content */}
                        <div className="p-4">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                                {/* Personal Information */}
                                <div className="space-y-4">
                                    <h2 className="text-lg font-bold text-gray-900 font-['Crimson_Text']">
                                        Personal Information
                                    </h2>
                                    
                                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                        <div className="flex items-center">
                                            <User className="w-4 h-4 text-[#dc2626] mr-3" />
                                            <div>
                                                <p className="text-xs text-gray-500">Full Name</p>
                                                <p className="font-medium text-sm text-gray-900">
                                                    {profileData?.name || 'Not provided'}
                                                </p>
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-center">
                                            <Mail className="w-4 h-4 text-[#dc2626] mr-3" />
                                            <div>
                                                <p className="text-xs text-gray-500">Email Address</p>
                                                <p className="font-medium text-sm text-gray-900">
                                                    {profileData?.email || 'Not provided'}
                                                </p>
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-center">
                                            <Phone className="w-4 h-4 text-[#dc2626] mr-3" />
                                            <div>
                                                <p className="text-xs text-gray-500">Phone Number</p>
                                                <p className="font-medium text-sm text-gray-900">
                                                    {profileData?.phone || 'Not provided'}
                                                </p>
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-center">
                                            <Calendar className="w-4 h-4 text-[#dc2626] mr-3" />
                                            <div>
                                                <p className="text-xs text-gray-500">Joined On</p>
                                                <p className="font-medium text-sm text-gray-900">
                                                    {profileData?.createdAt 
                                                        ? formatDate(profileData.createdAt) 
                                                        : 'Not available'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Business Address */}
                                <div className="space-y-4">
                                    <h2 className="text-lg font-bold text-gray-900 font-['Crimson_Text']">
                                        Business Address
                                    </h2>
                                    
                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <div className="space-y-3">
                                            <div className="flex items-start">
                                                <MapPin className="w-4 h-4 text-[#dc2626] mr-3 mt-0.5" />
                                                <div>
                                                    <p className="text-xs text-gray-500">Street Address</p>
                                                    <p className="font-medium text-sm text-gray-900">
                                                        {profileData?.address?.street || 'Not provided'}
                                                    </p>
                                                </div>
                                            </div>
                                            
                                            <div className="flex items-start">
                                                <MapPin className="w-4 h-4 text-[#dc2626] mr-3 mt-0.5" />
                                                <div>
                                                    <p className="text-xs text-gray-500">City & State</p>
                                                    <p className="font-medium text-sm text-gray-900">
                                                        {profileData?.address?.city && profileData?.address?.state
                                                            ? `${profileData.address.city}, ${profileData.address.state}`
                                                            : 'Not provided'}
                                                    </p>
                                                </div>
                                            </div>
                                            
                                            <div className="flex items-start">
                                                <MapPin className="w-4 h-4 text-[#dc2626] mr-3 mt-0.5" />
                                                <div>
                                                    <p className="text-xs text-gray-500">Country & Postal Code</p>
                                                    <p className="font-medium text-sm text-gray-900">
                                                        {profileData?.address?.country && profileData?.address?.pinCode
                                                            ? `${profileData.address.country} - ${profileData.address.pinCode}`
                                                            : 'Not provided'}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AdminProfile;